import { AnyAction } from 'redux'
import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { get, isEmpty, isNil, upperCase } from 'lodash'
import { DateTime } from 'luxon'
import { PlanType } from '@labrav/react-components'
import {
  errorLogger,
  getErrorMessages,
  LoadingTypes,
  RequestFailureMessage,
  startLoading,
  stopLoading,
  updateAppAlert,
  emitSalesforceResourceSaved,
} from '@labrav/common'

import type { AppDispatch, RootState } from '../../../store/types'
import { PartnerType, type UserState } from '../../../types/external'
import type { Stakeholders } from '../../../types/flyout'
import {
  type AzureOfferPlan,
  type Dimension,
  type FieldMetadata,
  type Installments,
  type MetaData,
  type OfferRecipients,
  type OfferStakeholders,
  type PrivateOffer,
  EulaType,
  GcpPostPaymentTypes,
  OfferRecurrentPricingType,
  OfferStatus,
  PaymentType,
  PlanTypeGCP,
  RecurrentPriceModeType,
  ScheduleType,
} from '../types'
import type {
  CloudMarketplace,
  Product,
  ProductDimension,
} from '../../productsListing/types'
import { ContainerPricingModel } from '../../productsListing/types'
import { PrivateOfferCreationActionTypes } from './actionTypes'
import {
  type OffersWithErrors,
  initialPricesObject,
  type KeyValue,
} from './types'
import { createErrorObjectForPlan } from './utils/createErrorObjectForPlan'
import { getErrorObjectForAField } from './utils/rowFactories'
import {
  applyPurchaseTermsMetaData,
  normalizeAzurePurchaseTermsForSubmit,
  normalizeVariableStartDate,
  toAcceptByDateForSubmit,
  toAcceptByDatePickerDate,
  toPurchaseStartMonthForSubmit,
  toPurchaseStartMonthPickerDate,
} from './utils/microsoftPurchaseTermsUtil'
import {
  getDimensionsBasedOnProductType,
  getUpdatedDimensionsForDraftPO,
  getEntitlementOption,
  getContainerOfferDimensions,
  getFilteredDimensions,
  getZeroDollarPricing,
  returnOnlySpecificDimensionsField,
} from './utils/dimensionHelper'
import {
  applyAwsRenewalTermsToPayload,
  getFilteredInstallments,
  normalizeAwsRenewalTermsFromApi,
} from './utils/installmentHelper'
import {
  getArrayFieldsForValidation,
  getContainerServiceLengthValueFromOffer,
  convertDateFromAPIResponse,
  getOfferRecipients,
  getOfferMetaData,
  getSaasContractDurationPayload,
  NET_PAYMENT_TERMS_AWS_DEFAULT,
} from './utils/helperFunctions'
import {
  getPlansforPayload,
  getAzurePricingObject,
  getFilteredPlans,
} from './utils/microsoftPlanHelper'
import {
  getConvertedGCPPlanDataForOffer,
  getGCPPlanDataBasedOnType,
} from './utils/gcpPlanHelper'
import {
  mappingReferralObjectToForm,
  getFieldMetadataFromTemplate,
} from './utils/mappingManagerHelper'
import {
  buildAzureContainerPrivateOfferPayload,
  isAzureContainerProduct,
  mapContainerAzurePlansFromApi,
  normalizeAzurePricingType,
} from '../../../components/MicrosoftFormComponent/ContainerPlan/containerPlanUtils'
import {
  buildAzureVmPrivateOfferPayload,
  isAzureVmProduct,
  mapVmAzurePlansFromApi,
} from '../../../components/MicrosoftFormComponent/VmPlan/vmPlanUtils'
import {
  getSinglePrivateOffer,
  patchPrivateOffersData,
  postPrivateOffer,
} from '../../../api/marketplace/index'
import {
  patchAzurePrivateOffersData,
  patchAzureContainerPrivateOffer,
  patchAzureVmPrivateOffer,
  postAzurePrivateOffer,
  postAzureContainerPrivateOffer,
  postAzureVmPrivateOffer,
} from '../../../api/azure/index'
import {
  getGcpSinglePrivateOffer,
  putGCPPrivateOffersData,
} from '../../../api/gcp/index'
import { getAllPartnerUserEmails } from '../../../api/partnerUsers'
import {
  getMarketplaceDefaultOfferMarketplaceMapping,
  getMarketplaceDefaultOffersReferralMapping,
} from '../../../api/mappingManager'
import { findProductByProductRef } from '../../../utils/privateOfferOrResaleHelperFunctions'
import { isOneHourContractDuration } from '../../../utils/containerOfferHelperFunctions'
import { getAzureSinglePrivateOfferApi } from '../utils/privateOfferCloudApis'
import {
  calculateUsageEndDateFromDuration,
  calculateUsageDurationFromDates,
  getUsageDuration,
  getPricingModel,
  parseDurationFromServiceLength,
} from '../../../utils/amiPrivateOfferUtil'
import { getAzureMappedPlans } from '../../../utils/getAzureMappedPlans'
import { defaultCrmType } from '../../../utils/constants'
import {
  privateOfferCreationSuccess,
  privateOfferSaveSuccess,
} from '../../../common/utils/messagesContants'
import emptyPrivateOffer from '../../../mocks/emptyPrivateOffers.json'
import emptyAzurePrivateOffer from '../../../mocks/emptyAzurePrivateOffer.json'
import emptyGcpPrivateOffer from '../../../mocks/emptyGcpPrivateOffer.json'

const validateFields = [
  'installments',
  'offerExpirationDate',
  'dimensions',
  'offerRecipients',
  'companyName',
  'awsAccountId',
  'emailCustomMessage',
  'privateOfferName',
  'serviceLength',
  'metaData',
  'subscriptionEndDate',
  'billingAccountId',
  'offerStartDate',
  'variableStartDate',
  'acceptBy',
  'plans',
  'preparedBy',
  'agreementStartDate',
]

export type setPrivateOfferInCreationProps = {
  productId: string
  data: Record<string, unknown>
  errors: Record<string, unknown>
  noOfTimesSaved: number
}

export const setPrivateOfferInCreation = ({
  productId,
  data,
  errors,
  noOfTimesSaved = 0,
}: setPrivateOfferInCreationProps) =>
  ({
    type: PrivateOfferCreationActionTypes.SET_CREATE_PRIVATE_OFFERS_DATA,
    payload: { productId, privateOfferData: data, errors, noOfTimesSaved },
  }) as unknown as AnyAction

export const updatePrivateOfferInCreation =
  (data: KeyValue, productId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloudType = getState().productListingFilters.cloudMarketplace
    await dispatch(setPrivateOffersData(data, productId))
    if (validateFields.includes(data.key)) {
      await dispatch(
        setErrorsOnPrivateOfferCreation(
          data.key as string,
          productId,
          cloudType
        )
      )
    }
  }

export const updateStandardDimensionsOnPrivateOffer = (
  index: number,
  productId: string,
  dimension: Dimension
) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_STANDARD_DIMENSIONS_ON_PRIVATE_OFFER,
  payload: { index, productId, dimension },
})

export const setPrivateOffersData = (data: KeyValue, productId: string) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_PRIVATE_OFFERS,
  payload: { privateOfferData: data, productId },
})

export const addRowsOnPrivateOffer = (
  key: string,
  productId: string,
  type = 'standard'
) => ({
  type: PrivateOfferCreationActionTypes.ADD_ROWS_ON_PRIVATE_OFFER,
  payload: { key, productId, type },
})

export const deleteRowsOnPrivateOffer =
  (key: string, index: number, productId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloudType = getState().productListingFilters.cloudMarketplace
    await dispatch(deleteRows(key, index, productId))
    if (validateFields.includes(key)) {
      await dispatch(setErrorsOnPrivateOfferCreation(key, productId, cloudType))
    }
  }

export const deleteRows = (key: string, index: number, productId: string) => ({
  type: PrivateOfferCreationActionTypes.DELETE_ROWS_ON_PRIVATE_OFFER,
  payload: { key, index, productId },
})

export const updatevaluesInArrayOfPrivateOffers =
  (key: string, index: number, privateOfferData: KeyValue, productId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState()
    const cloudType = state.productListingFilters.cloudMarketplace
    const standardDimension = state.productsListing[
      cloudType
    ].products.rows.filter(val => val.productId === productId)[0]
      .productDimensions

    await dispatch(
      setValuesInArrayOfPrivateOffers(
        key,
        index,
        privateOfferData,
        productId
      ) as unknown as AnyAction
    )
    if (validateFields.includes(key)) {
      const timeZone = getState().userProfile.userProfile.timeZone || 'UTC'
      await dispatch(
        setErrorsOnPrivateOfferCreation(
          key,
          productId,
          cloudType,
          standardDimension,
          privateOfferData.key,
          index,
          timeZone
        ) as unknown as AnyAction
      )
    }
  }

export const setValuesInArrayOfPrivateOffers = (
  key: string,
  index: number,
  privateOfferData: KeyValue,
  productId: string
) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_VALUES_OF_ARRAY_ON_PRIVATE_OFFER,
  payload: { key, privateOfferData, index, productId },
})

export const updateNoOfTimesSaved = (productId: string) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_NUMBER_OF_TIMES_SAVED_ON_PRIVATE_OFFER,
  payload: productId,
})

export const setFieldMetaData = (
  productId: string,
  fieldMetadata: FieldMetadata
) => ({
  type: PrivateOfferCreationActionTypes.SET_FIELD_METADATA,
  payload: { productId, fieldMetadata },
})

export const setErrorsOnPrivateOfferCreation = (
  key: string,
  productId: string,
  cloudType: CloudMarketplace,
  standardDimensions?: ProductDimension[],
  field?: string,
  index?: number,
  timeZone?: string
) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_PRIVATE_OFFER_CREATION_ERRORS,
  payload: {
    key,
    productId,
    cloudType,
    field,
    index,
    timeZone,
    standardDimensions,
  },
})

export const setSingleFieldErrorOnPrivateOfferCreation = (
  key: string,
  productId: string,
  errMsg: string
) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_PRIVATE_OFFER_SINGLE_FIELD_ERROR,
  payload: {
    key,
    productId,
    errMsg,
  },
})

export const addEulaFilesOnPrivateOffer = (
  productId: string,
  eulaFiles: string[]
) => ({
  type: PrivateOfferCreationActionTypes.ADD_EULA_FILES_ON_PRIVATE_OFFERS,
  payload: { productId, eulaFiles },
})

export const updateHasBeenSent = (hasBeenSent: boolean, productId: string) => ({
  type: PrivateOfferCreationActionTypes.UPDTE_PRIVATE_OFFER_HAS_BEEN_SENT,
  payload: { hasBeenSent, productId },
})

export const createOfferErrors = (
  data: Record<string, unknown>,
  arrayFieldsForOfferValidations: string[],
  isResale = false
) => {
  const errors = Object.keys(data).map(key => {
    if (arrayFieldsForOfferValidations.includes(key)) {
      const temp: Array<Record<string, unknown>> = [] as Array<
        Record<string, unknown>
      >
      if (data[key] !== null) {
        ;(data[key] as Array<Record<string, unknown>>).forEach(field => {
          if (isResale && key === 'dimensions') {
            temp.push({ ...initialPricesObject })
          } else if (key === 'plans') {
            temp.push({
              ...createErrorObjectForPlan(field as unknown as AzureOfferPlan),
            })
          } else {
            temp.push({
              ...getErrorObjectForAField(
                key,
                key === 'dimension'
                  ? (field as unknown as Dimension).type === 'custom'
                    ? 'custom'
                    : 'standard'
                  : ''
              ),
            })
          }
        })
        return temp
      } else {
        return []
      }
    } else return ''
  })
  const errorObject: Record<string, unknown> = {}
  Object.keys(data).forEach((key, index) => {
    errorObject[key] = errors[index]
  })
  return errorObject
}

export const clearPrivateOfferInCreation = (productId: string) => ({
  type: PrivateOfferCreationActionTypes.CLEAR_PRIVATE_OFFER_IN_CREATION,
  payload: productId,
})

export const updateProductPlanOnPrivateOffer = (
  index: number,
  productId: string,
  plan: AzureOfferPlan,
  keyForErrorCheck?: string,
  subIndex?: number,
  meter?: string,
  deleteBillingTerms?: boolean
) => ({
  type: PrivateOfferCreationActionTypes.UPDATE_PRODUCT_PLAN_ON_PRIVATE_OFFER,
  payload: {
    index,
    productId,
    plan,
    keyForErrorCheck,
    subIndex,
    meter,
    deleteBillingTerms,
  },
})

const getInitialPrivateOfferDataBasedOnCloud = (
  cloud: CloudMarketplace,
  product: Product,
  getState: () => RootState,
  crmOpportunityId?: string,
  labraNewUiRevamp?: boolean,
  partnerType: PartnerType = PartnerType.User
) => {
  const crm_opportunity_id = crmOpportunityId ?? null
  const email_custom_message = product?.emailCustomMessage || null
  const { email, firstName, lastName, givenName, familyName } =
    getState().userProfile.userProfile
  const { offerRecipients } =
    getState().privateOffersInCreation.currentPrivateOfferInCreation[
      product?.productId
    ] || []
  const hasCreatorData = !isEmpty(firstName) && !isEmpty(email)
  const creatorData = hasCreatorData
    ? {
        creator_data: { creator_name: firstName, creator_email: email },
      }
    : {}
  switch (cloud) {
    case 'AWS': {
      const planType = product?.productType

      const initialOfferRecipients = labraNewUiRevamp
        ? (offerRecipients && offerRecipients.length) > 0
          ? offerRecipients
          : [{ email: '', title: '', firstName: '', lastName: '' }]
        : []

      return {
        ...emptyPrivateOffer,
        crm_opportunity_id,
        email_custom_message,
        ...creatorData,
        entitlement_option: getEntitlementOption(labraNewUiRevamp, product),
        eula_type:
          labraNewUiRevamp && product.productType === PlanType.CONTAINER
            ? 'PUBLIC'
            : planType === PlanType.PROFESSIONAL_SERVICES
              ? 'CUSTOM'
              : 'STANDARD',
        awsProductId: product?.productId,
        ...(planType === PlanType.SAAS_CONTRACT
          ? { variable_start_date: true, agreement_start_date: null }
          : {}),
        dimensions: getDimensionsBasedOnProductType(
          product,
          labraNewUiRevamp ? true : false
        ),
        offer_recipients: initialOfferRecipients,
        ...(labraNewUiRevamp && product.productType === PlanType.CONTAINER
          ? {
              container_offer_serviceLength:
                product?.containerPricingModel !==
                ContainerPricingModel.CONTRACT
                  ? '1Y'
                  : '12M',
            }
          : {}),
        ...(labraNewUiRevamp &&
        (product.productType === PlanType.CONTAINER ||
          product.productType === PlanType.AMI)
          ? {
              installments: [
                { paymentDate: '', amount: null, currency: 'USD' },
              ],
              usageDuration: null,
            }
          : {}),
        ...((labraNewUiRevamp &&
          (product.productType === PlanType.CONTAINER ||
            product.productType === PlanType.SAAS_CONTRACT)) ||
        product.productType === PlanType.SAAS_SUBSCRIPTION
          ? { meta_data: [{ key: '', value: '' }] }
          : {}),
        ...(labraNewUiRevamp &&
        (product.productType === PlanType.CONTAINER ||
          product.productType === PlanType.SAAS_CONTRACT ||
          product.productType === PlanType.SAAS_SUBSCRIPTION ||
          product.productType === PlanType.AMI)
          ? {
              offerStakeholders: crmOpportunityId
                ? []
                : creatorData.creator_data?.creator_email
                  ? [
                      {
                        email: creatorData.creator_data?.creator_email,
                        name: creatorData.creator_data?.creator_name,
                      },
                    ]
                  : [],
              customOfferStakeholders: [],
            }
          : {}),
        ...(labraNewUiRevamp &&
        (product.productType === PlanType.CONTAINER ||
          product.productType === PlanType.SAAS_CONTRACT ||
          product.productType === PlanType.AMI)
          ? {
              tcv: null,
              flexiblePaymentType: ScheduleType.CUSTOM,
              firstInstallmentDate: new Date().toISOString().split('T')[0],
            }
          : {}),
        ...(product.productType === PlanType.AMI
          ? {
              durationType: 'DAYS',
              durationLength: '',
              usageEndDate: null,
              serviceLength: undefined,
              pricingModel: 'upfront-payment',
            }
          : {}),
        metaData: [{ key: '', value: '' }],
      }
    }
    case 'AZURE': {
      return {
        ...camelize(emptyAzurePrivateOffer),
        crmOpportunityId: crm_opportunity_id,
        emailCustomMessage: email_custom_message,
        offerRecipients:
          (offerRecipients && offerRecipients.length) > 0
            ? offerRecipients
            : [{ email: '', title: '', firstName: '', lastName: '' }],
        preparedBy: email || '',
        offerStakeholders: email
          ? [
              {
                firstName: firstName || givenName || '',
                lastName: lastName || familyName || '',
                email: email || '',
              },
            ]
          : [],
      }
    }
    case 'GCP': {
      return {
        ...camelize(emptyGcpPrivateOffer),
        crmOpportunityId: crm_opportunity_id,
        emailCustomMessage: email_custom_message,
        individualDiscount: false,
        offerStakeholders: [
          {
            firstName: firstName || givenName || '',
            lastName: lastName || familyName || '',
            email: email || '',
          },
        ],
      }
    }
  }
}

const mapOfferAWSOffer = (
  offerResonse: PrivateOffer,
  getState: () => RootState,
  product?: Product,
  partnerType: PartnerType = PartnerType.User,
  usersEmails?: string[]
) => {
  const {
    offerStakeholders,
    offerRecipients,
    metaData,
    installments,
    flexiblePaymentType,
    flexiblePaymentTerms,
    containerOfferServiceLength,
    auditJson,
  } = offerResonse
  const installmentsInfo =
    (installments || []).length > 0
      ? installments
      : [{ paymentDate: '', amount: null, currency: 'USD' }]

  const totalContractValue =
    installments && installments.length > 0
      ? installments.reduce((total, installment) => {
          return total + (installment?.amount || 0)
        }, 0) || null
      : null

  const firstInstallmentDate =
    installments && installments.length > 0
      ? installments[0]?.paymentDate || null
      : null

  const users = usersEmails?.length
    ? usersEmails
    : (getState().userList[partnerType]?.userList?.users || []).map(
        ({ email }) => email
      )
  const stackHolderExisting =
    (offerStakeholders || [])?.filter(
      item => !users?.length || users.includes(item.email)
    ) || []
  const stackHolderCustom =
    (offerStakeholders || [])?.filter(
      item => users?.length > 0 && !users.includes(item.email)
    ) || []

  return {
    offerStakeholders: [...stackHolderExisting],
    customOfferStakeholders: [...stackHolderCustom],
    auditJson,
    offerRecipients:
      (offerRecipients || []).length > 0
        ? offerRecipients
        : [{ email: '', title: '', firstName: '', lastName: '' }],
    metaData: (metaData || []).length > 0 ? metaData : [{ key: '', value: '' }],
    ...(product?.productType === PlanType.CONTAINER ||
    product?.productType === PlanType.SAAS_CONTRACT ||
    product?.productType === PlanType.AMI
      ? {
          installments: installmentsInfo,
          flexiblePaymentType:
            flexiblePaymentType ||
            (product?.productType === PlanType.AMI
              ? ScheduleType.MONTHLY
              : ScheduleType.CUSTOM),
          tcv: totalContractValue,
          firstInstallmentDate: firstInstallmentDate,
          ...(product?.productType === PlanType.AMI
            ? {
                pricingModel: getPricingModel(
                  flexiblePaymentTerms,
                  containerOfferServiceLength
                ),
              }
            : {}),
        }
      : {}),
    ...(product?.productType === PlanType.CONTAINER
      ? {
          ...getContainerOfferDimensions(offerResonse, product),
          containerOfferServiceLength: getContainerServiceLengthValueFromOffer(
            offerResonse,
            product
          ),
        }
      : {}),
  }
}

export const getPrivateOfferDataFromAPIResponse = async (
  cloud: CloudMarketplace,
  {
    partnerId,
    privateOfferId,
    crmOpportunityId,
    productId,
    product,
    userList,
    usersEmails,
  }: {
    productId: string
    partnerId: string
    privateOfferId: string
    crmOpportunityId?: string
    product?: Product
    userList?: UserState[]
    usersEmails?: string[]
  },
  dispatch: AppDispatch,
  getState?: () => RootState
) => {
  try {
    switch (cloud) {
      case 'AWS': {
        const { data } = await getSinglePrivateOffer(
          partnerId,
          privateOfferId,
          crmOpportunityId
        )
        const {
          productName,
          links,
          agreementEndDate: apiAgreementEndDate,
          ...remainingData
        } = camelize(data || {})

        const offerExpirationDate = convertDateFromAPIResponse(
          (remainingData as PrivateOffer)?.offerExpirationDate
        )
        const usageDuration = (remainingData as PrivateOffer)?.usageDuration
        const calculatedUsageEndDate = calculateUsageEndDateFromDuration(
          offerExpirationDate,
          usageDuration
        )
        const containerOfferServiceLength = (remainingData as PrivateOffer)
          ?.containerOfferServiceLength
        const { durationType, durationLength } = parseDurationFromServiceLength(
          containerOfferServiceLength
        )

        return normalizeAwsRenewalTermsFromApi({
          ...remainingData,
          netPaymentTerms: isEmpty(
            (remainingData as PrivateOffer)?.netPaymentTerms
          )
            ? NET_PAYMENT_TERMS_AWS_DEFAULT
            : String((remainingData as PrivateOffer)?.netPaymentTerms),
          awsProductId: productId,
          ...(product?.productType === PlanType.AMI
            ? {
                offerExpirationDate,
                ...(calculatedUsageEndDate
                  ? { usageEndDate: calculatedUsageEndDate }
                  : {}),
                ...(durationType ? { durationType } : {}),
                ...(durationLength !== null ? { durationLength } : {}),
              }
            : {
                offerExpirationDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer)?.offerExpirationDate
                ),
              }),
          ...(product?.productType === PlanType.SAAS_SUBSCRIPTION
            ? {
                subscriptionEndDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer)?.subscriptionEndDate as string
                ),
              }
            : {}),
          ...(product?.productType === PlanType.SAAS_CONTRACT
            ? {
                agreementStartDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer)?.agreementStartDate
                ),
                agreementEndDate: convertDateFromAPIResponse(
                  apiAgreementEndDate as string
                ),
                subscriptionEndDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer).subscriptionEndDate ||
                    apiAgreementEndDate
                ),
              }
            : {}),
          ...(product?.productType === PlanType.SAAS_CONTRACT &&
          remainingData.variableStartDate === false
            ? {
                agreementStartDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer)?.agreementStartDate
                ),
              }
            : {}),
          dimensions: getUpdatedDimensionsForDraftPO(
            remainingData.dimensions || [],
            productId
          ),
          ...((product?.productType === PlanType.CONTAINER ||
            product?.productType === PlanType.SAAS_CONTRACT ||
            product?.productType === PlanType.SAAS_SUBSCRIPTION ||
            product?.productType === PlanType.AMI) &&
          getState
            ? {
                ...mapOfferAWSOffer(
                  remainingData as PrivateOffer,
                  getState,
                  product,
                  PartnerType.User,
                  usersEmails
                ),
              }
            : {}),
        })
      }
      case 'AZURE': {
        const isVmProduct = isAzureVmProduct(product)
        const isContainerProduct = isAzureContainerProduct(product)
        const getPrivateOffer = getAzureSinglePrivateOfferApi(product)
        const { data } = await getPrivateOffer(
          partnerId,
          privateOfferId,
          crmOpportunityId,
          productId
        )
        const { plans, ...offerData } = data
        const { links, id, eulaFiles, offerStakeholders, ...remainingData } =
          camelize(offerData || {})
        const normalizedRemainingData = {
          ...remainingData,
          privateOfferName:
            (remainingData as PrivateOffer)?.privateOfferName ||
            (remainingData as { name?: string })?.name,
        }
        const users = usersEmails?.length
          ? usersEmails
          : (userList || []).map(({ email }) => email)
        const existingStakeHolder = !users?.length
          ? [...(offerStakeholders || [])]
          : (offerStakeholders || [])?.filter((item: Stakeholders) =>
              users.includes(item.email)
            ) || []
        const stackHolderCustom = !users?.length
          ? []
          : (offerStakeholders || [])?.filter(
              (item: Stakeholders) => !users.includes(item.email)
            ) || []
        const payloadPlans = plans?.length
          ? isVmProduct
            ? mapVmAzurePlansFromApi({
                plans,
                product: product as Product,
              })
            : isContainerProduct
              ? mapContainerAzurePlansFromApi({
                  plans,
                  product: product as Product,
                })
              : getAzureMappedPlans({ plans, product: product as Product })
          : [
              {
                displayName: '',
                planId: '',
                offerPricingType: 'saasNewCustomizedPlans',
                description: '',
                labraPlanId: '',
                discountPercentage: null,
                pricing: {
                  recurrentPrice: {
                    recurrentPriceMode: '',
                    priceInputOption: 'usd',
                    prices: [],
                  },
                },
              },
            ]

        const offerExpirationDate = convertDateFromAPIResponse(
          (remainingData as PrivateOffer)?.offerExpirationDate
        )
        const usageDuration = (remainingData as PrivateOffer)?.usageDuration
        const calculatedUsageEndDate = calculateUsageEndDateFromDuration(
          offerExpirationDate,
          usageDuration
        )
        const containerOfferServiceLength = (remainingData as PrivateOffer)
          ?.containerOfferServiceLength
        const { durationType, durationLength } = parseDurationFromServiceLength(
          containerOfferServiceLength
        )

        return {
          ...normalizedRemainingData,
          draft: true,
          variableStartDate: normalizeVariableStartDate(
            (normalizedRemainingData as PrivateOffer)?.variableStartDate
          ),
          eulaFiles: eulaFiles || [],
          privateOfferId: privateOfferId,
          azurePricingType: normalizeAzurePricingType(
            (plans || []).length > 0
              ? plans[0].discount_type || plans[0].discountType
              : ''
          ),
          plans: payloadPlans,
          eulaType:
            (normalizedRemainingData as PrivateOffer)?.eulaType ||
            EulaType.STANDARD,
          ...(product?.productType === PlanType.AMI
            ? {
                offerExpirationDate,
                ...(calculatedUsageEndDate
                  ? { usageEndDate: calculatedUsageEndDate }
                  : {}),
                ...(durationType ? { durationType } : {}),
                ...(durationLength !== null ? { durationLength } : {}),
              }
            : {
                offerExpirationDate: convertDateFromAPIResponse(
                  (normalizedRemainingData as PrivateOffer)?.offerExpirationDate
                ),
              }),

          acceptBy: toAcceptByDatePickerDate(
            (normalizedRemainingData as PrivateOffer)?.acceptBy,
            getState?.().userProfile.userProfile.timeZone ||
              getState?.().userProfile.userProfile.defaultTimeZone ||
              'UTC'
          ),
          offerStartDate: toPurchaseStartMonthPickerDate(
            (normalizedRemainingData as PrivateOffer)?.offerStartDate,
            getState?.().userProfile.userProfile.timeZone ||
              getState?.().userProfile.userProfile.defaultTimeZone ||
              'UTC'
          ),
          existingContractRenewal: (normalizedRemainingData as PrivateOffer)
            ?.existingContractRenewal,
          offerStakeholders: [...existingStakeHolder],
          customOfferStakeholders: [...stackHolderCustom],
          offerRecipients:
            (normalizedRemainingData as PrivateOffer)?.offerRecipients &&
            (normalizedRemainingData as PrivateOffer)?.offerRecipients.length >
              0
              ? (normalizedRemainingData as PrivateOffer).offerRecipients
              : [{ email: '', title: '', firstName: '', lastName: '' }],
          metaData:
            (normalizedRemainingData as PrivateOffer)?.metaData &&
            (normalizedRemainingData as PrivateOffer)?.metaData.length > 0
              ? (normalizedRemainingData as PrivateOffer).metaData
              : [{ key: '', value: '' }],
        }
      }
      case 'GCP': {
        const { data } = await getGcpSinglePrivateOffer(
          partnerId,
          privateOfferId,
          crmOpportunityId,
          productId
        )
        const {
          links,
          eulaFiles,
          labraOfferId,
          contractDuration,
          paymentFrequency,
          labraPlanId,
          offerMetadata,
          plan: notUsedPlan,
          tcv,
          installmentSchedule,
          individualDiscount,
          ...remainingData
        } = camelize(data)

        const offerPlan = getConvertedGCPPlanDataForOffer(data, product)

        const offerExpirationDate = convertDateFromAPIResponse(
          (remainingData as PrivateOffer)?.offerExpirationDate
        )
        const usageDuration = (remainingData as PrivateOffer)?.usageDuration
        const calculatedUsageEndDate = calculateUsageEndDateFromDuration(
          offerExpirationDate,
          usageDuration
        )
        const containerOfferServiceLength = (remainingData as PrivateOffer)
          ?.containerOfferServiceLength
        const { durationType, durationLength } = parseDurationFromServiceLength(
          containerOfferServiceLength
        )

        return {
          ...remainingData,
          eulaFile: eulaFiles || [],
          ...(product?.productType === PlanType.AMI
            ? {
                offerExpirationDate,
                ...(calculatedUsageEndDate
                  ? { usageEndDate: calculatedUsageEndDate }
                  : {}),
                ...(durationType ? { durationType } : {}),
                ...(durationLength !== null ? { durationLength } : {}),
              }
            : {
                offerExpirationDate: convertDateFromAPIResponse(
                  (remainingData as PrivateOffer)?.offerExpirationDate
                ),
              }),
          privateOfferId: labraOfferId,
          notes: offerMetadata?.notes,
          emailCustomMessage: offerMetadata?.emailCustomMessage,
          ...offerPlan,
        }
      }
    }
  } catch (err: any) {
    dispatch(
      updateAppAlert({
        message: getErrorMessages([RequestFailureMessage])(
          err.response as AxiosResponse<ErrorResponse>
        ),
        messageType: 'ERROR',
        autoClose: true,
      })
    )
  }
}

export const getCurrentPrivateOfferInCreation =
  ({
    productId,
    partnerId,
    privateOfferId,
    crmOpportunityId,
    labraNewUiRevamp = false,
  }: {
    productId: string
    partnerId: string
    privateOfferId: string
    crmOpportunityId?: string
    labraNewUiRevamp?: boolean
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloudMarketplace = getState().productListingFilters.cloudMarketplace

    const selectedProduct =
      getState().productsListing[cloudMarketplace]?.productDetail
    const product = selectedProduct
      ? selectedProduct
      : findProductByProductRef(
          getState().productsListing[cloudMarketplace].products.rows,
          productId
        ) || ({} as Product)
    const effectiveProductId = product.productId || productId

    await dispatch(clearPrivateOfferInCreation(effectiveProductId))
    let offerData = {}

    if (isEmpty(privateOfferId)) {
      offerData = {
        ...getInitialPrivateOfferDataBasedOnCloud(
          cloudMarketplace,
          product,
          getState,
          crmOpportunityId,
          labraNewUiRevamp
        ),
      }
    } else {
      const { userList } = getState().userList.user
      const usersEmails = await getAllPartnerUserEmails(partnerId)
      offerData = await getPrivateOfferDataFromAPIResponse(
        cloudMarketplace,
        {
          productId: effectiveProductId,
          partnerId,
          privateOfferId,
          crmOpportunityId,
          product,
          userList: userList.users,
          usersEmails,
        },
        dispatch,
        getState
      )
    }
    let plans = [] as Record<string, unknown>[]
    if (cloudMarketplace === 'AZURE') {
      plans = get(offerData, 'plans', []) || []
    }
    const finalData = camelize({
      ...offerData,
    })
    const errors =
      cloudMarketplace === 'GCP' || cloudMarketplace === 'AZURE'
        ? {}
        : createOfferErrors(
            finalData,
            getArrayFieldsForValidation(cloudMarketplace)
          )
    await dispatch(
      setPrivateOfferInCreation({
        productId: effectiveProductId,
        data: {
          ...finalData,
          ...(cloudMarketplace === 'AZURE' ? { plans } : {}),
        },
        errors,
        noOfTimesSaved: isEmpty(privateOfferId) ? 0 : 2,
      })
    )
  }

type PrivateOfferSpecificData = {
  containerOfferServiceLength: string
  usageDuration: string
  customOfferStakeholders: OfferStakeholders[]
  dimensions: Dimension[]
}

const getContainerOfferTransformForFormik = (
  privateOffer: OffersWithErrors,
  privateOfferSpecificData: PrivateOfferSpecificData,
  selectedProduct: Product
) => {
  const {
    containerOfferServiceLength,
    usageDuration,
    customOfferStakeholders,
    dimensions,
  } = privateOfferSpecificData

  const containerOfferServiceLengthValue = (containerOfferServiceLength ||
    '') as string
  const contractDurationVal =
    selectedProduct.containerPricingModel === ContainerPricingModel.CONTRACT
      ? containerOfferServiceLengthValue +
        (containerOfferServiceLengthValue.endsWith('M') ? '' : 'M')
      : containerOfferServiceLengthValue +
        (containerOfferServiceLengthValue.endsWith('Y') ||
        containerOfferServiceLengthValue.endsWith('H')
          ? ''
          : 'D')

  return {
    usageDuration:
      !isNil(usageDuration) && usageDuration !== '' ? usageDuration : null,
    offerRecipients: getOfferRecipients(privateOffer?.offerRecipients || []),
    metaData: getOfferMetaData(privateOffer?.metaData || []),
    offerStakeholders: privateOffer?.offerStakeholders?.concat(
      (customOfferStakeholders || []) as OfferStakeholders[]
    ),
    containerOfferServiceLength: contractDurationVal,
    dimensions: isOneHourContractDuration(
      privateOffer?.containerOfferServiceLength || ''
    )
      ? ((dimensions || []) as Dimension[]).filter(
          dimensionItem => dimensionItem.isAdditionalUsageAllowed
        )
      : dimensions,
  }
}

export const getPayloadDataBasedOnCloud = ({
  cloud,
  privateOfferData,
  getState,
  saveAsDraft,
  crmOpportunityId,
  creatorData,
  selectedProduct,
}: {
  cloud: CloudMarketplace
  privateOfferData: OffersWithErrors
  // Only userProfile is read; keep this narrow so host-app getState is assignable.
  getState: () => Pick<RootState, 'userProfile'>
  saveAsDraft?: boolean
  crmOpportunityId?: string
  creatorData?: {
    creatorName: string
    creatorEmail: string
  }
  selectedProduct?: Product
}) => {
  const { errors, hasBeenSent, noOfTimesSaved, ...privateOffer } =
    privateOfferData
  const {
    givenName: firstName,
    familyName: lastName,
    email,
  } = getState().userProfile.userProfile
  switch (cloud) {
    case 'AWS': {
      let privateOfferToBeSent = {} as Record<string, unknown>
      const shouldUseFormStakeholders =
        selectedProduct?.productType === PlanType.SAAS_CONTRACT ||
        selectedProduct?.productType === PlanType.SAAS_SUBSCRIPTION ||
        selectedProduct?.productType === PlanType.AMI
      const offerStakeholders = shouldUseFormStakeholders
        ? [
            ...((privateOffer?.offerStakeholders as OfferStakeholders[]) || []),
            ...(Array.isArray(privateOffer?.customOfferStakeholders)
              ? privateOffer.customOfferStakeholders
              : []),
          ]
        : [
            {
              firstName: firstName || '',
              lastName: lastName || '',
              email: email || '',
            },
          ]
      if (privateOffer.flexiblePaymentTerms) {
        const dimensions = (privateOffer?.dimensions || []).map(dimension => {
          if (dimension.isAdditionalUsageAllowed) {
            const { type, ...remainingDimension } = dimension
            return remainingDimension
          }
          const { type, price, ...updatedDimension } = dimension
          return updatedDimension
        })
        const { installments, flexiblePaymentType, ...updatedOffer } =
          privateOffer
        privateOfferToBeSent = {
          ...updatedOffer,
          dimensions,
          installmentInfo: [...installments!],
          flexiblePaymentType,
        }
      } else {
        const dimensions = (privateOffer?.dimensions || []).map(dimension => {
          if (dimension.isAdditionalUsageAllowed) {
            const { type, prices, ...remainingDimension } = dimension
            return remainingDimension
          }
          const { type, prices, quantity, ...updatedDimension } = dimension
          return updatedDimension
        })
        const { installments, ...updatedOffer } = privateOffer
        privateOfferToBeSent = {
          ...updatedOffer,
          dimensions,
          installmentInfo: installments,
        }
      }
      const zeroDollarPricing = getZeroDollarPricing(
        privateOffer?.dimensions || []
      )
      privateOfferToBeSent.zeroDollarPricing = zeroDollarPricing
      const isSaasContract =
        selectedProduct?.productType === PlanType.SAAS_CONTRACT
      const durationPayload = isSaasContract
        ? getSaasContractDurationPayload({
            contractDurationMode: privateOffer.contractDurationMode,
            serviceLength: privateOffer.serviceLength,
            subscriptionEndDate: privateOffer.subscriptionEndDate,
            agreementEndDate: privateOffer.agreementEndDate,
            agreementStartDate: privateOffer.agreementStartDate,
            variableStartDate: privateOffer.variableStartDate,
          })
        : null
      if (durationPayload) {
        privateOfferToBeSent.serviceLength = durationPayload.serviceLength
        privateOfferToBeSent.agreementStartDate =
          durationPayload.agreementStartDate
        if (durationPayload.agreementEndDate) {
          privateOfferToBeSent.agreementEndDate =
            durationPayload.agreementEndDate
        } else {
          delete privateOfferToBeSent.agreementEndDate
        }
        if (durationPayload.subscriptionEndDate) {
          privateOfferToBeSent.subscriptionEndDate =
            durationPayload.subscriptionEndDate
        } else {
          delete privateOfferToBeSent.subscriptionEndDate
        }
      }
      privateOfferToBeSent.dimensions = returnOnlySpecificDimensionsField(
        getFilteredDimensions(
          privateOfferToBeSent.dimensions as unknown as Dimension[]
        ),
        privateOfferToBeSent?.currency as string
      )
      const installments = getFilteredInstallments(
        (privateOfferToBeSent.installmentInfo ||
          []) as unknown as Installments[]
      )
      privateOfferToBeSent.installmentInfo = installments.map(inst => ({
        ...inst,
        currency: privateOfferToBeSent.currency,
      }))

      if (
        selectedProduct?.productType === PlanType.AMI &&
        privateOfferData?.pricingModel !== 'installment-plan'
      ) {
        privateOfferToBeSent.installmentInfo = null
        privateOfferToBeSent.flexiblePaymentTerms = false
        privateOfferToBeSent.flexiblePaymentType = null
        privateOfferToBeSent.firstInstallmentDate = null
      }

      privateOfferToBeSent.offerExpirationDate = isEmpty(
        privateOfferData.offerExpirationDate
      )
        ? null
        : privateOfferData.offerExpirationDate
      privateOfferToBeSent.emailCustomMessage = isEmpty(
        privateOfferData.emailCustomMessage
      )
        ? null
        : privateOfferData.emailCustomMessage
      privateOfferToBeSent.netPaymentTerms =
        isEmpty(privateOfferData.netPaymentTerms) ||
        privateOfferData.netPaymentTerms === NET_PAYMENT_TERMS_AWS_DEFAULT
          ? null
          : privateOfferData.netPaymentTerms
      privateOfferToBeSent.offerStakeholders = [...offerStakeholders]

      // Calculate usageDuration as difference in days between offerExpirationDate and usageEndDate
      if (
        selectedProduct?.productType === PlanType.AMI &&
        privateOfferToBeSent.usageEndDate &&
        !isEmpty(privateOfferToBeSent.usageEndDate)
      ) {
        const calculatedUsageDuration = calculateUsageDurationFromDates({
          offerExpirationDate:
            privateOfferToBeSent?.offerExpirationDate as string,
          usageEndDate: privateOfferToBeSent.usageEndDate as string,
        })
        if (calculatedUsageDuration !== null) {
          privateOfferToBeSent.usageDuration = calculatedUsageDuration
        }
      }
      const {
        usageDuration,
        offerRecipients,
        variableStartDate,
        agreementStartDate,
        customOfferStakeholders,
        dimensions,
        containerOfferServiceLength,
        metaData: metaDataValue,
        tcv,
        flexiblePaymentType,
        firstInstallmentDate,
        offerStartDate,
        usageEndDate,
        pricingModel,
        durationType,
        durationLength,
        pricingToolType,
        contractDurationMode,
        ...remainingOfferToBesent
      } = privateOfferToBeSent
      const awsOfferPayload = {
        ...remainingOfferToBesent,
        offerRecipients: ((offerRecipients as OfferRecipients[]) || [])?.filter(
          (recipient: OfferRecipients) =>
            recipient?.email ||
            recipient?.firstName ||
            recipient?.lastName ||
            recipient?.title
        ),
        // metaData: metaDataValue?.map((item) => ({ key: item?.key, value: item?.value })),
        ...(selectedProduct?.productType === PlanType.SAAS_CONTRACT
          ? {
              variableStartDate,
              agreementStartDate: isEmpty(agreementStartDate)
                ? null
                : agreementStartDate,
            }
          : {}),
        ...(selectedProduct?.productType === PlanType.SAAS_CONTRACT &&
        privateOffer.flexiblePaymentTerms
          ? {
              ...(flexiblePaymentType ? { flexiblePaymentType } : {}),
            }
          : {}),
        ...(selectedProduct?.productType === PlanType.CONTAINER
          ? {
              ...getContainerOfferTransformForFormik(
                privateOfferData,
                {
                  usageDuration: usageDuration as string,
                  customOfferStakeholders:
                    customOfferStakeholders as OfferStakeholders[],
                  dimensions: dimensions as Dimension[],
                  containerOfferServiceLength:
                    containerOfferServiceLength as string,
                },
                selectedProduct
              ),
            }
          : selectedProduct?.productType === PlanType.AMI
            ? {
                dimensions: privateOfferData?.dimensions?.map(
                  ({ id, quantity, price, ...dimension }) => ({
                    ...dimension,
                    quantity: quantity || null,
                    price: price || null,
                  })
                ),
                usageDuration: (() => {
                  const computedUsageDuration = getUsageDuration(
                    usageDuration,
                    privateOfferData?.offerExpirationDate,
                    privateOfferData?.usageEndDate
                  )

                  if (privateOfferData?.pricingModel === 'installment-plan') {
                    return null
                  }

                  return computedUsageDuration
                })(),
                containerOfferServiceLength:
                  containerOfferServiceLength || null,
                ...(tcv !== null && tcv !== undefined ? { tcv } : {}),
                ...(firstInstallmentDate ? { firstInstallmentDate } : {}),
                ...(flexiblePaymentType ? { flexiblePaymentType } : {}),
              }
            : { dimensions }),
        metaData: ((metaDataValue as MetaData[]) || [])
          ?.filter(item => item?.key || item?.value)
          ?.map((item: MetaData) => ({ key: item?.key, value: item?.value })),
      }

      return applyAwsRenewalTermsToPayload(
        privateOffer as PrivateOffer,
        awsOfferPayload
      )
    }
    case 'AZURE': {
      let privateOfferToBeSent = {} as Record<string, unknown>
      const {
        plans,
        crmOrderObjectId,
        variableStartDate,
        existingContractRenewal,
        eulaFiles,
        billingAccountId,
        companyName,
        metaData,
        offerRecipients,
        privateOfferName,
        emailCustomMessage,
        azurePricingType,
        privateOfferId,
        preparedBy,
        customOfferStakeholders,
        offerStakeholders,
        offerDescription,
      } = privateOffer
      const cloudProductId = selectedProduct?.cloudMarketplaceProductId

      const normalizedPurchaseTerms = normalizeAzurePurchaseTermsForSubmit({
        variableStartDate,
        offerStartDate: privateOffer.offerStartDate,
        offerExpirationDate: privateOffer.offerExpirationDate,
        purchaseTermsCustomized: Boolean(
          (
            privateOffer as PrivateOffer & {
              purchaseTermsCustomized?: boolean
            }
          ).purchaseTermsCustomized
        ),
      })

      const purchaseTermsTimeZone =
        getState().userProfile.userProfile.timeZone ||
        getState().userProfile.userProfile.defaultTimeZone ||
        'UTC'

      const offerStartDate = isEmpty(normalizedPurchaseTerms.offerStartDate)
        ? null
        : toPurchaseStartMonthForSubmit(
            normalizedPurchaseTerms.offerStartDate,
            purchaseTermsTimeZone
          )

      const acceptBy = isEmpty(privateOffer.acceptBy)
        ? null
        : toAcceptByDateForSubmit(privateOffer.acceptBy, purchaseTermsTimeZone)

      const offerExpirationDate = isEmpty(
        normalizedPurchaseTerms.offerExpirationDate
      )
        ? null
        : DateTime.fromISO(normalizedPurchaseTerms.offerExpirationDate!).toISO()

      const normalizedVariableStartDate =
        normalizedPurchaseTerms.variableStartDate

      if (isAzureVmProduct(selectedProduct)) {
        return {
          ...(privateOfferId ? { privateOfferId } : {}),
          ...buildAzureVmPrivateOfferPayload({
            privateOfferName,
            crmOrderObjectId,
            crmOpportunityId,
            cloudProductId,
            billingAccountId,
            variableStartDate: normalizedVariableStartDate,
            existingContractRenewal,
            offerExpirationDate,
            acceptBy,
            offerStartDate,
            preparedBy: preparedBy || creatorData?.creatorEmail,
            eulaType: privateOffer.eulaType,
            offerDescription: offerDescription ?? undefined,
            companyName: companyName ?? undefined,
            emailCustomMessage: emailCustomMessage ?? undefined,
            offerRecipients: offerRecipients || [],
            offerStakeholders: offerStakeholders || [],
            customOfferStakeholders: customOfferStakeholders || [],
            plans: plans || [],
            azurePricingType,
          }),
        }
      }

      if (isAzureContainerProduct(selectedProduct)) {
        return {
          ...(privateOfferId ? { privateOfferId } : {}),
          ...buildAzureContainerPrivateOfferPayload({
            privateOfferName,
            crmOrderObjectId,
            crmOpportunityId,
            cloudProductId,
            billingAccountId,
            variableStartDate: normalizedVariableStartDate,
            existingContractRenewal,
            offerExpirationDate,
            acceptBy,
            offerStartDate,
            preparedBy: preparedBy || creatorData?.creatorEmail,
            eulaType: privateOffer.eulaType,
            offerDescription: offerDescription ?? undefined,
            companyName: companyName ?? undefined,
            emailCustomMessage: emailCustomMessage ?? undefined,
            offerRecipients: offerRecipients || [],
            offerStakeholders: offerStakeholders || [],
            customOfferStakeholders: customOfferStakeholders || [],
            plans: plans || [],
            azurePricingType,
          }),
        }
      }

      const processMeters = (
        meters: Record<string, any>
      ): Record<string, any> => {
        const snakeizedMeters: Record<string, any> = {}

        Object.keys(meters).forEach(meterKey => {
          snakeizedMeters[meterKey] = snakeize(meters[meterKey])
        })

        return snakeizedMeters
      }

      const payloadPlans = plans
        ?.filter(item => item?.labraPlanId)
        .map(
          ({
            originalPricing,
            pricing,
            discountPercentage,
            offerPricingType: _offerPricingType,
            ...remainingPlan
          }) => {
            const isItPerUser =
              pricing?.recurrentPrice?.recurrentPriceMode ===
              RecurrentPriceModeType.PERUSER

            const { userLimits, ...remainingRecurrentPrice } =
              pricing?.recurrentPrice

            const hasInstallmentSchedules =
              (pricing?.recurrentPrice?.prices?.[0]?.installments?.schedule
                ?.length ?? 0) > 0

            let resolvedPrices = remainingRecurrentPrice?.prices

            if (hasInstallmentSchedules) {
              resolvedPrices = [
                {
                  ...remainingRecurrentPrice?.prices?.[0],
                  paymentOption: {
                    ...remainingRecurrentPrice?.prices?.[0]?.paymentOption,
                    type: 'flexible',
                  },
                  pricePerPaymentInUsd: undefined,
                },
              ]
            }

            return {
              ...snakeize(remainingPlan),
              discount_type: azurePricingType,
              offer_pricing_type:
                _offerPricingType ||
                OfferRecurrentPricingType.SAASNEWCUSTOMIZEDPLANS,
              pricing: {
                recurrent_price: snakeize({
                  ...remainingRecurrentPrice,
                  ...(isItPerUser ? { userLimits } : {}),
                  price_input_option: 'usd',
                  prices: resolvedPrices,
                }),
                custom_meters: {
                  ...pricing?.customMeters,
                  meters: processMeters(pricing?.customMeters?.meters || {}),
                  price_input_option: 'usd',
                },
              },
            }
          }
        )
      privateOfferToBeSent = {
        crmOrderObjectId,
        crmOpportunityId,
        privateOfferId,
        privateOfferName,
        privateOfferType: 'customerPromotion',
        offerPricingType: 'saasNewCustomizedPlans',
        offerDescription,
        offerStartDate,
        offerExpirationDate,
        acceptBy,
        ...(preparedBy || creatorData?.creatorEmail
          ? { preparedBy: preparedBy || creatorData?.creatorEmail }
          : {}),
        eulaType: privateOffer.eulaType,
        eulaFiles,
        billingAccountId,
        cloudProductId,
        pricing: [],
        offerRecipients: getOfferRecipients(offerRecipients || []),
        companyName,
        emailCustomMessage,
        metaData: getOfferMetaData(
          applyPurchaseTermsMetaData(
            metaData || [],
            Boolean(
              (
                privateOffer as PrivateOffer & {
                  purchaseTermsCustomized?: boolean
                }
              ).purchaseTermsCustomized
            )
          ) as MetaData[]
        ),
        plans: payloadPlans,
        labraProductId: selectedProduct?.productId,
        productName: selectedProduct?.productName,
        offerStakeholders: crmOpportunityId
          ? []
          : offerStakeholders?.concat(
              (customOfferStakeholders || [])?.map(({ email }) => ({
                email: email,
                firstName: '',
                lastName: '',
                title: '',
              }))
            ),
        ...(normalizedVariableStartDate !== undefined
          ? { variableStartDate: normalizedVariableStartDate }
          : {}),
        existingContractRenewal: existingContractRenewal || false,
        ...(saveAsDraft
          ? { offerStatus: OfferStatus.PRIVATE_OFFER_IN_DRAFT }
          : { offerStatus: OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS }),
      }
      return privateOfferToBeSent
    }
    case 'GCP': {
      const {
        companyName,
        buyerCloudBillingId,
        eulaFile,
        autoRenew,
        emailCustomMessage,
        offerStakeholders,
        notes,
        eulaType,
        plan,
        renewalCount,
        privateOfferName,
        offerExpirationDate,
        offerRecipients,
        serviceStartDate,
        privateOfferId,
        labraPlanFeatures,
        dealType,
      } = privateOffer

      const tcvValue =
        plan?.planType !== PlanTypeGCP.USAGE
          ? {
              ...(plan?.paymentType &&
              GcpPostPaymentTypes.includes(plan?.paymentType) &&
              !isNil(plan?.serviceLength) &&
              !isNil(plan?.flatFeePrice)
                ? {
                    tcv: Number(
                      plan?.serviceLength * plan?.flatFeePrice
                    ).toFixed(2),
                  }
                : {}),
              ...(plan?.paymentType === PaymentType.CUSTOM
                ? { tcv: plan?.tcv }
                : {}),
            }
          : {}

      return {
        draft: saveAsDraft,
        createViaApi: true,
        crmOpportunityId,
        serviceStartDate,
        offerStatus: OfferStatus.PRIVATE_OFFER_CREATION_IN_PROGRESS,
        buyerCloudBillingId,
        privateOfferName,
        eulaType,
        paymentFrequency:
          plan?.planId && plan?.paymentType ? plan?.paymentType : undefined,
        companyName,
        eulaFiles: eulaFile,
        autoRenew,
        offerExpirationDate: DateTime.fromISO(
          offerExpirationDate as string
        ).toISO(),
        offerRecipients,
        offerStakeholders,
        offerMetadata: {
          notes,
          emailCustomMessage,
        },
        contractDuration: plan?.serviceLength,
        labraProductId: selectedProduct?.productId,
        cloud: 'GCP',
        labraPlanId: plan?.planId ? plan?.planId : undefined,
        labraPlanFeatures,
        ...tcvValue,
        ...(plan?.installmentSchedule
          ? { installmentSchedule: upperCase(plan?.installmentSchedule) }
          : {}),
        individualDiscount: plan?.individualDiscount,
        plan:
          plan && plan?.planId
            ? getGCPPlanDataBasedOnType(plan, plan?.individualDiscount)
            : {},
        flexiblePaymentTerms: plan?.paymentType === PaymentType.CUSTOM,
        ...(privateOfferId
          ? { labraOfferId: privateOfferId, privateOfferId }
          : {}),
        ...(autoRenew ? { renewalCount: renewalCount } : {}),
        ...(dealType ? { dealType: dealType } : {}),
      }
    }
    case 'REDHAT': {
      return {}
    }
  }
}

export const postOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  OfferPayload: {
    partnerId: string
    data: Record<string, unknown>
    saveAsDraft?: boolean
    isContainerProduct?: boolean
    isVmProduct?: boolean
  }
) => {
  switch (cloud) {
    case 'AWS': {
      return await postPrivateOffer(OfferPayload)
    }
    case 'AZURE': {
      if (OfferPayload.isVmProduct) {
        return await postAzureVmPrivateOffer(OfferPayload)
      }

      return OfferPayload.isContainerProduct
        ? await postAzureContainerPrivateOffer(OfferPayload)
        : await postAzurePrivateOffer(OfferPayload)
    }
    case 'GCP': {
      return await putGCPPrivateOffersData(OfferPayload)
    }
  }
}

export const patchOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  OfferPayload: {
    partnerId: string
    data: Record<string, unknown>
    saveAsDraft?: boolean
    privateOfferId: string
    isContainerProduct?: boolean
    isVmProduct?: boolean
  }
) => {
  const {
    partnerId,
    data,
    saveAsDraft,
    privateOfferId,
    isContainerProduct,
    isVmProduct,
  } = OfferPayload
  switch (cloud) {
    case 'AWS': {
      return await patchPrivateOffersData(
        partnerId,
        privateOfferId as string,
        data,
        saveAsDraft
      )
    }
    case 'AZURE': {
      if (isVmProduct) {
        return await patchAzureVmPrivateOffer({
          partnerId,
          privateOfferId,
          data,
          saveAsDraft,
        })
      }

      return isContainerProduct
        ? await patchAzureContainerPrivateOffer({
            partnerId,
            privateOfferId,
            data,
            saveAsDraft,
          })
        : await patchAzurePrivateOffersData(
            partnerId,
            privateOfferId as string,
            data,
            saveAsDraft
          )
    }
    case 'GCP': {
      return await putGCPPrivateOffersData({
        data,
        partnerId,
      })
    }
  }
}

export const sendPrivateOffer =
  ({
    partnerId,
    productId,
    saveAsDraft,
    crmOpportunityId,
    crmObjectLink,
    formikOffer,
    isV2,
    creatorData,
  }: {
    partnerId: string
    productId: string
    saveAsDraft?: boolean
    crmOpportunityId?: string
    crmObjectLink?: string
    formikOffer?: OffersWithErrors
    isV2?: boolean
    autoSave?: boolean
    creatorData?: {
      creatorName: string
      creatorEmail: string
    }
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!isV2 || saveAsDraft) {
      await dispatch(startLoading(LoadingTypes.GENERAL))
    } else {
      await dispatch(startLoading(LoadingTypes.SUBMIT_FLYOUT_Offer))
    }
    try {
      const selectedCloud = getState().productListingFilters.cloudMarketplace
      const privateOfferData =
        selectedCloud === 'GCP' || isV2
          ? formikOffer!
          : getState().privateOffersInCreation.currentPrivateOfferInCreation[
              productId
            ]
      const selectedProduct = getState().productsListing[
        selectedCloud
      ].products?.rows.find(product => product.productId === productId)

      const isContainerProduct = isAzureContainerProduct(selectedProduct)
      const isVmProduct = isAzureVmProduct(selectedProduct)

      const privateOfferToBeSent = getPayloadDataBasedOnCloud({
        cloud: selectedCloud,
        privateOfferData,
        getState,
        saveAsDraft,
        crmOpportunityId,
        creatorData,
        selectedProduct,
      })

      let crmMetadata = {} as Record<string, unknown>
      if (crmOpportunityId && crmObjectLink) {
        crmMetadata = {
          crmType: defaultCrmType,
          crmObjectLink,
        }
      }

      const { privateOfferId, plans, ...privateOfferToBeSentWithoutId } =
        privateOfferToBeSent
      if (saveAsDraft && !isContainerProduct && !isVmProduct) {
        privateOfferToBeSent.draft = true
      }

      if (isEmpty(privateOfferId)) {
        const payload = {
          partnerId,
          data: snakeize({
            ...privateOfferToBeSentWithoutId,
            ...(!isContainerProduct && !isVmProduct && crmOpportunityId
              ? { crmMetadata }
              : {}),
            ...(!isContainerProduct && !isVmProduct && creatorData
              ? { creatorData }
              : {}),
            ...(selectedCloud === 'AZURE' ? { plans } : {}),
          }),
          saveAsDraft,
          isContainerProduct,
          isVmProduct,
        }
        const response = await postOfferBasedOnCloud(selectedCloud, payload)
        if (response && response.data) {
          if ((selectedCloud === 'GCP' || isV2) && formikOffer) {
            await updateFormikOfferAfterAPICall(
              formikOffer,
              dispatch,
              productId,
              0,
              camelize(response.data).privateOfferId ||
                (camelize(response.data).id as string)
            )
          } else {
            await dispatch(
              updatePrivateOfferInCreation(
                {
                  key: 'privateOfferId',
                  value:
                    camelize(response.data).privateOfferId ||
                    camelize(response.data).id,
                },
                productId
              ) as unknown as AnyAction
            )
          }
        }
      } else {
        const patchPayload = {
          partnerId,
          data: {
            ...snakeize({
              ...privateOfferToBeSentWithoutId,
              ...(selectedCloud !== 'AZURE' ? { draft: saveAsDraft } : {}),
              ...(!isContainerProduct && !isVmProduct && crmOpportunityId
                ? { crmMetadata }
                : {}),
              ...(selectedCloud === 'GCP'
                ? { labraOfferId: privateOfferId }
                : {}),
              ...(!isContainerProduct && !isVmProduct && creatorData
                ? { creatorData }
                : {}),
              ...(selectedCloud === 'AZURE' ? { plans } : {}),
            }),
          },
          privateOfferId: privateOfferId as string,
          saveAsDraft,
          isContainerProduct,
          isVmProduct,
        }
        const { subscription, ...restData } = patchPayload.data
        patchPayload.data = restData
        await patchOfferBasedOnCloud(selectedCloud, patchPayload)
        if ((selectedCloud === 'GCP' || isV2) && formikOffer) {
          await updateFormikOfferAfterAPICall(
            formikOffer,
            dispatch,
            productId,
            2,
            privateOfferId as string
          )
        }
      }
      if (!saveAsDraft) {
        await dispatch(updateHasBeenSent(true, productId))
      } else {
        await dispatch(updateNoOfTimesSaved(productId))
      }
      dispatch(
        updateAppAlert({
          message: saveAsDraft
            ? privateOfferSaveSuccess
            : privateOfferCreationSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )

      emitSalesforceResourceSaved({
        resourceType: 'offer',
        partnerId,
        opportunityId: crmOpportunityId || null,
        offer: {
          id:
            privateOfferToBeSent.privateOfferId || formikOffer?.privateOfferId,
          draft: !!saveAsDraft,
          hasBeenSent: !saveAsDraft,
          productId,
        },
      })
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (!isV2 || saveAsDraft) {
        await dispatch(stopLoading(LoadingTypes.GENERAL))
      } else {
        await dispatch(stopLoading(LoadingTypes.SUBMIT_FLYOUT_Offer))
      }
    }
  }

const updateFormikOfferAfterAPICall = async (
  formikOffer: OffersWithErrors,
  dispatch: AppDispatch,
  productId: string,
  noOfTimesSaved: number,
  privateOfferId?: string
) => {
  const { errors, ...remainingOffer } = formikOffer as OffersWithErrors
  const purchaseTermsCustomized = (
    remainingOffer as OffersWithErrors & {
      purchaseTermsCustomized?: boolean
    }
  ).purchaseTermsCustomized

  await dispatch(
    setPrivateOfferInCreation({
      productId,
      data: {
        ...remainingOffer,
        privateOfferId: privateOfferId,
        metaData: applyPurchaseTermsMetaData(
          remainingOffer.metaData,
          Boolean(purchaseTermsCustomized)
        ) as MetaData[],
      },
      errors: {},
      noOfTimesSaved,
    })
  )
}

export const getDefaultPrivateOfferInCreation =
  ({
    productId,
    crmOpportunityId,
    labraNewUiRevamp = false,
    crmId,
    crmObjectId,
    templateId,
    accountId,
    isHubspot,
  }: {
    productId: string
    crmOpportunityId?: string
    labraNewUiRevamp?: boolean
    crmId: string
    crmObjectId: string
    templateId?: string
    accountId?: string
    isHubspot?: boolean
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_DEFAULT_OFFERS))
    try {
      await dispatch(clearPrivateOfferInCreation(productId))
      const cloudMarketplace = getState().productListingFilters.cloudMarketplace
      const product =
        getState().productsListing[cloudMarketplace].products.rows.find(
          product => product.productId === productId
        ) || ({} as Product)
      const mappingTemplate = getState().marketplaceMappingManager.templateData

      const { data } = isHubspot
        ? await getMarketplaceDefaultOffersReferralMapping(
            crmId,
            crmObjectId,
            productId,
            templateId
          )
        : await getMarketplaceDefaultOfferMarketplaceMapping(
            crmId,
            templateId,
            accountId,
            crmObjectId
          )

      let extractedData = {}
      let fieldMetadata = {}

      if (isHubspot) {
        fieldMetadata = data?.field_metadata
        const [dynamicKey] = Object.keys(data).filter(
          key => key !== 'field_metadata'
        )
        extractedData = data[dynamicKey]
        if (extractedData && 'renewal' in extractedData) {
          extractedData.renewal =
            extractedData.renewal === 'true' ? true : false
        }
      } else {
        const referralObject = data?.referral_object
        const selectedTemplate = mappingTemplate?.templateData?.fieldMappings
        const dimensions = data?.dimensions
        extractedData = mappingReferralObjectToForm(
          productId,
          referralObject,
          dimensions,
          mappingTemplate,
          product,
          cloudMarketplace
        )
        fieldMetadata = getFieldMetadataFromTemplate(
          selectedTemplate,
          referralObject,
          cloudMarketplace
        )
      }

      let offerData = {}

      offerData = {
        ...getInitialPrivateOfferDataBasedOnCloud(
          cloudMarketplace,
          product,
          getState,
          crmOpportunityId,
          labraNewUiRevamp
        ),
        ...extractedData,
      }
      const finalData = camelize({
        ...offerData,
      })
      const errors =
        cloudMarketplace === 'GCP'
          ? {}
          : createOfferErrors(
              finalData,
              getArrayFieldsForValidation(cloudMarketplace)
            )
      await dispatch(
        setPrivateOfferInCreation({
          productId,
          data: { ...finalData },
          errors,
          noOfTimesSaved: 0,
        })
      )
      await dispatch(
        setFieldMetaData(productId, camelize({ ...fieldMetadata }))
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GET_DEFAULT_OFFERS))
    }
  }

/** Maps core Salesforce referral fields + contract duration to private-offer form keys. */
export const privateOfferCreationTestExports = {
  getPlansforPayload,
  getAzurePricingObject,
  getFilteredPlans,
}
